import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";

/**
 * GET /v2/contracts?event_id={id} — the contract entity/entities for one
 * event. Used only to resolve a contract's id so the staff-side
 * ContractTimeline (append-only audit trail, V2 Builder 3) can be rendered
 * on the event detail page; the timeline itself fetches by contract id.
 * Minimal projection — we only need id + status here.
 */
const EventContractSchema = z.object({
  id: z.string(),
  event_id: z.string(),
  status: z.string(),
});
const EventContractListSchema = z.array(EventContractSchema);
export type EventContract = z.infer<typeof EventContractSchema>;

export const eventContractKeys = {
  all: ["eventContracts"] as const,
  byEvent: (eventId: string | undefined) => [...eventContractKeys.all, eventId] as const,
};

export function useEventContracts(eventId: string | undefined) {
  return useQuery({
    queryKey: eventContractKeys.byEvent(eventId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/contracts`, { params: { event_id: eventId } });
      return validateResponse(EventContractListSchema, data, "GET /v2/contracts?event_id");
    },
    enabled: !!eventId,
    staleTime: 15 * 1000,
  });
}
